import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { Button, Popover } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import BuscarExpedienteModal from "./containers/pages/buscar.jsx";
import logoPng from "./assets/img/logo2.png";
import "./styles.css";
import "./assets/css/fontawesome-free-6.1.0-web/css/all.min.css";
import { actionNotificacionesGet } from "./redux/actions/notificaciones/notificaciones";
import useSocket from "./hooks/useSocket";
import { relativeTime } from "./utils/relativeTime";
import VerticalSidebar from "./components/navigation/VerticalSidebar.jsx";
import SolicitarCambioButton from "./components/solicitudes-cambio/SolicitarCambioButton.jsx";
import { usePuedeSolicitarCambios } from "./context/PermisosContext";

export default function ElectronHeader({ hideUserPopover }) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const concSlice = useSelector((state) => state.notificaciones || {});
  // Vista vertical fija para todos los usuarios (el selector horizontal/vertical
  // en Configuración > General se eliminó, ya no hay preferencia por usuario).
  const isVertical = true;

  const [notifOpen, setNotifOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [isBuscarOpen, setIsBuscarOpen] = useState(false);
  const [appMenuOpen, setAppMenuOpen] = useState(false);

  const [histIdx, setHistIdx] = useState(() => window.history.state?.idx ?? 0);
  const maxHistIdxRef = useRef(histIdx);

  const pendingRefreshRef = useRef(null);
  const appMenuRef = useRef(null);

  const isElectron = useMemo(() => {
    return typeof window !== "undefined" && !!window.electronAPI;
  }, []);

  const puedeSolicitarCambios = usePuedeSolicitarCambios();

  const isLoginPage = location.pathname.includes("/login");
  const canShowUser = !isLoginPage;

  const userEmail = localStorage.getItem("email") || "—";
  const userInitials = useMemo(() => {
    const name = (localStorage.getItem("email") || "JD").trim();
    const parts = name.split(/\s+/).filter(Boolean);
    const a = parts[0]?.[0] || "J";
    const b = parts[0]?.[1] || "D";
    return (a + b).toUpperCase();
  }, []);

  useEffect(() => {
    if (hideUserPopover) return;
    dispatch(actionNotificacionesGet());
  }, [dispatch, hideUserPopover]);

  useEffect(() => {
    if (hideUserPopover && userOpen) setUserOpen(false);
  }, [hideUserPopover, userOpen]);

  const handleSocketEvent = useCallback(
    (msg) => {
      if (msg?.type !== "NOTIFICACION_INVALIDATE") return;
      if (pendingRefreshRef.current) clearTimeout(pendingRefreshRef.current);
      pendingRefreshRef.current = setTimeout(() => {
        dispatch(actionNotificacionesGet());
        pendingRefreshRef.current = null;
      }, 800);
    },
    [dispatch]
  );

  useSocket("event", handleSocketEvent);

  useEffect(() => {
    if (!appMenuOpen) return;
    const handler = (e) => {
      if (appMenuRef.current && !appMenuRef.current.contains(e.target)) {
        setAppMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [appMenuOpen]);

  useEffect(() => {
    return () => {
      if (pendingRefreshRef.current) clearTimeout(pendingRefreshRef.current);
    };
  }, []);

  useEffect(() => {
    const idx = window.history.state?.idx ?? 0;
    if (idx > maxHistIdxRef.current) maxHistIdxRef.current = idx;
    setHistIdx(idx);
  }, [location]);

  const canGoBack = histIdx > 0;
  const canGoForward = histIdx < maxHistIdxRef.current;

  const pathname = location.pathname || "/";

  const isCentroActive =
    pathname.startsWith("/centro") || pathname.startsWith("/materias/laboral/centro-conciliacion");
  const isAgendaActive = pathname.startsWith("/agenda");
  const isTribunalActive = pathname.startsWith("/materias/laboral/tribunal");
  const isDesvinculacionesActive = pathname.startsWith("/materias/laboral/desvinculaciones");
  const isHomeActive = pathname.startsWith("/home");
  const isEmpresaActive = pathname.startsWith("/empresas");
  const isCorresponsalActive = pathname.startsWith("/corresponsales");
  const isJuntaActive = pathname.startsWith("/materias/laboral/junta-conciliacion");
  const isAbogadosActive = pathname.startsWith("/abogados");
  const isAutoridadesActive = pathname.startsWith("/autoridades");
  const isConfiguracionActive = pathname.startsWith("/configuracion");
  const isUsuariosActive = pathname.startsWith("/usuarios");
  const isInformesActive = pathname.startsWith("/informes");
  const isNotificacionesActive = pathname.startsWith("/notificaciones");

  const moduleTitle = isHomeActive
    ? "Inicio"
    : isCentroActive
    ? "Centro Conciliación"
    : isJuntaActive
    ? "Junta de Conciliación"
    : isTribunalActive
    ? "Tribunal"
    : isDesvinculacionesActive
    ? "Desvinculaciones"
    : isAbogadosActive
    ? "Abogados"
    : isAgendaActive
    ? "Agenda"
    : isInformesActive
    ? "Informes"
    : isAutoridadesActive
    ? "Autoridades"
    : isConfiguracionActive
    ? "Configuración"
    : isCorresponsalActive
    ? "Corresponsales"
    : isEmpresaActive
    ? "Empresas"
    : isUsuariosActive
    ? "Usuarios"
    : isNotificacionesActive
    ? "Notificaciones"
    : "";

  const links = [
    { label: "Inicio", onClick: () => navigate("/home"), active: isHomeActive },
    {
      label: "Centro Conciliación",
      onClick: () => navigate("/materias/laboral/centro-conciliacion"),
      active: isCentroActive,
    },
    {
      label: "Junta de Conciliación",
      onClick: () => navigate("/materias/laboral/junta-conciliacion"),
      active: isJuntaActive,
    },
    {
      label: "Tribunal",
      onClick: () => navigate("/materias/laboral/tribunal"),
      active: isTribunalActive,
    },
  ];

  const notifications = (concSlice?.data?.items || []).map((item) => {
    let type = "info";
    if (Number(item?.urgente) === 1) {
      type = "danger";
    } else if ((item?.nombre_modulo || "").toLowerCase().includes("tribunal")) {
      type = "warn";
    } else if ((item?.nombre_tipo_notificacion || "").toLowerCase().includes("document")) {
      type = "neutral";
    }

    let iconClass = "bi-folder2-open eh-notif-icon info";
    if (type === "danger") iconClass = "bi-exclamation-triangle-fill eh-notif-icon danger";
    else if (type === "warn") iconClass = "bi-clock-fill eh-notif-icon warn";
    else if (type === "neutral") iconClass = "bi-file-earmark-text eh-notif-icon neutral";

    const currentUserId = Number(concSlice?.data?.user_id);
    const yaLeyeron = Array.isArray(item?.usuarios_leyeron) ? item.usuarios_leyeron : [];
    const leidoPorUsuarioActual = yaLeyeron.some((u) => Number(u?.id_user) === currentUserId);

    return {
      id: item.id,
      type,
      iconClass,
      title: item.nombre_tipo_notificacion || "Notificación",
      desc: item.descripcion || "Sin descripción",
      meta: `${relativeTime(item.fecha_notificacion || item.datetime)} • ${
        leidoPorUsuarioActual ? "Leído" : "No leído"
      }`,
      unread: !leidoPorUsuarioActual,
    };
  });

  const unreadCount = notifications.filter((n) => n.unread).length;

  const notifCard = (
    <div className="eh-notif-pop">
      <div className="eh-notif-head">
        <div className="eh-notif-title">Notificaciones</div>
        {unreadCount > 0 && (
          <span className="eh-notif-head-count">{unreadCount} nueva{unreadCount === 1 ? "" : "s"}</span>
        )}
      </div>
      <div className="eh-notif-list">
        {notifications.length > 0 ? (
          notifications.map((n) => (
            <div
              key={n.id}
              className={`eh-notif-item ${n.type}`}
              onClick={() => {
                setNotifOpen(false);
                navigate(`/notificaciones/${n.id}`);
              }}
              style={{ cursor: "pointer" }}
            >
              <div className="eh-notif-ico-wrap">
                <i className={`bi ${n.iconClass}`} />
              </div>
              <div className="eh-notif-body">
                <div className="eh-notif-item-title">{n.title}</div>
                <div className="eh-notif-item-desc">{n.desc}</div>
                <div className="eh-notif-item-meta">{n.meta}</div>
              </div>
              {n.unread && <span className="eh-notif-dot" />}
            </div>
          ))
        ) : (
          <div className="eh-notif-item neutral">
            <div className="eh-notif-ico-wrap">
              <i className="bi bi-file-earmark-text eh-notif-icon neutral" />
            </div>
            <div className="eh-notif-body">
              <div className="eh-notif-item-title">Sin notificaciones</div>
              <div className="eh-notif-item-desc">No hay notificaciones registradas.</div>
              <div className="eh-notif-item-meta">—</div>
            </div>
          </div>
        )}
      </div>
      <div className="eh-notif-foot">
        <button
          className="eh-notif-link"
          type="button"
          onClick={() => {
            setNotifOpen(false);
            navigate("/notificaciones");
          }}
        >
          Ver todas las notificaciones
        </button>
      </div>
    </div>
  );

  const userCard = (
    <div className="eh-user-pop">
      <div className="eh-user-avatar">{userInitials}</div>
      <div className="eh-user-email">
        <small className="text-secondary">{userEmail}</small>
      </div>
      <div className="eh-user-actions">
        <Button className="w-100 mb-2" disabled>
          Settings
        </Button>
        <Button
          danger
          type="primary"
          className="w-100"
          onClick={() => {
            localStorage.removeItem("token");
            setUserOpen(false);
            navigate("/login");
          }}
        >
          Log out
        </Button>
      </div>
    </div>
  );

  /* ─── Modo vertical: sidebar + header compacto ─── */
  if (isVertical && !isLoginPage) {
    return (
      <>
        <VerticalSidebar />

        {/* Header compacto — sólo search + notificaciones */}
        <header className="eh-root eh-root--compact">
          <div className="eh-bar eh-bar--compact">
            <div className="eh-nav-history electron-no-drag">
              <button
                type="button"
                className={`eh-nav-history-btn${canGoBack ? "" : " disabled"}`}
                onClick={() => canGoBack && navigate(-1)}
                aria-label="Atrás"
                title="Atrás"
              >
                <i className="bi bi-chevron-left" />
              </button>
              <button
                type="button"
                className={`eh-nav-history-btn${canGoForward ? "" : " disabled"}`}
                onClick={() => canGoForward && navigate(1)}
                aria-label="Adelante"
                title="Adelante"
              >
                <i className="bi bi-chevron-right" />
              </button>
            </div>

            {moduleTitle && <div className="eh-module-title">{moduleTitle}</div>}

            <div className="eh-right electron-no-drag" style={{ marginLeft: "auto" }}>
              <button
                type="button"
                className="eh-ico-btn"
                aria-label="Buscar"
                onClick={() => setIsBuscarOpen(true)}
              >
                <i className="bi bi-search" />
              </button>

              <Popover
                open={notifOpen}
                onOpenChange={setNotifOpen}
                trigger="click"
                placement="bottomRight"
                overlayClassName="eh-popover"
                content={notifCard}
              >
                <button
                  type="button"
                  className="eh-ico-btn eh-bell"
                  aria-label="Notificaciones"
                  onClick={() => setUserOpen(false)}
                >
                  <i className="bi bi-bell-fill" />
                  {unreadCount > 0 && (
                    <span className="eh-badge">{unreadCount > 99 ? "99+" : unreadCount}</span>
                  )}
                </button>
              </Popover>

              {puedeSolicitarCambios && <SolicitarCambioButton tituloVista={moduleTitle} />}

              {isElectron && (
                <div className={`win-controls${puedeSolicitarCambios ? " win-controls--con-solicitud" : ""}`}>
                  <button className="electron-btn" title="Minimizar" onClick={() => window.electronAPI?.minimize?.()}>🗕</button>
                  <button className="electron-btn" title="Maximizar/Restaurar" onClick={() => window.electronAPI?.maximize?.()}>🗖</button>
                  <button className="electron-btn electron-btn-close" title="Cerrar" onClick={() => window.electronAPI?.close?.()}>✕</button>
                </div>
              )}
            </div>
          </div>
          <div className="eh-drag-overlay" />
        </header>

        <BuscarExpedienteModal
          open={isBuscarOpen}
          onClose={() => setIsBuscarOpen(false)}
          onSearch={(payload) => { console.log("Buscar expediente:", payload); }}
        />
      </>
    );
  }

  /* ─── Modo horizontal (por defecto) ─── */
  return (
    <>
      <header className="eh-root">
        <div className="eh-bar">
          <div className="eh-left">
            <div className="eh-brand">
              <img className="eh-logo" src={logoPng} alt="Logo" />
            </div>

            {isLoginPage !== true && (
              <nav className="eh-nav electron-no-drag">
                {links.map((l) => (
                  <button
                    key={l.label}
                    type="button"
                    className={`eh-link nav-link ${l.active ? "nav-link--active active" : ""}`}
                    onClick={l.onClick}
                  >
                    {l.label}
                  </button>
                ))}
              </nav>
            )}
          </div>

          <div className="eh-right electron-no-drag">
            {isLoginPage !== true && (
              <>
                <button
                  type="button"
                  className="eh-ico-btn"
                  aria-label="Buscar"
                  onClick={() => setIsBuscarOpen(true)}
                >
                  <i className="bi bi-search" />
                </button>

                <div className="eh-app-menu-wrapper" ref={appMenuRef}>
                  <button
                    type="button"
                    className={`eh-ico-btn eh-app-trigger${appMenuOpen ? " open" : ""}${(!appMenuOpen && (isEmpresaActive || isCorresponsalActive || isAgendaActive || isAbogadosActive || isAutoridadesActive || isConfiguracionActive || isDesvinculacionesActive || isUsuariosActive || isInformesActive)) ? " has-active" : ""}`}
                    aria-label="Accesos rápidos"
                    title="Accesos rápidos"
                    onClick={() => setAppMenuOpen((v) => !v)}
                  >
                    <i className="bi bi-grid-3x3-gap-fill" />
                  </button>

                  {appMenuOpen && (
                    <div className="eh-app-dropdown">
                      <span className="eh-app-dropdown-label">Accesos rápidos</span>
                      <div className="eh-app-items">
                        <button type="button" className={`eh-app-item${isAbogadosActive ? " active" : ""}`} onClick={() => { navigate("/abogados"); setAppMenuOpen(false); }}>
                          <span className="eh-app-item-icon eh-app-icon-abogados"><i className="fa-solid fa-people-group" /></span>
                          <span>Abogados</span>
                        </button>
                        <button type="button" className={`eh-app-item${isAgendaActive ? " active" : ""}`} onClick={() => { navigate("/agenda"); setAppMenuOpen(false); }}>
                          <span className="eh-app-item-icon eh-app-icon-agenda"><i className="fa-solid fa-calendar" /></span>
                          <span>Agenda</span>
                        </button>
                        <button type="button" className={`eh-app-item${isAutoridadesActive ? " active" : ""}`} onClick={() => { navigate("/autoridades"); setAppMenuOpen(false); }}>
                          <span className="eh-app-item-icon eh-app-icon-autoridades"><i className="fa-solid fa-landmark" /></span>
                          <span>Autoridades</span>
                        </button>
                        <button type="button" className={`eh-app-item${isConfiguracionActive ? " active" : ""}`} onClick={() => { navigate("/configuracion"); setAppMenuOpen(false); }}>
                          <span className="eh-app-item-icon eh-app-icon-configuracion"><i className="fa-solid fa-gears" /></span>
                          <span>Configuración</span>
                        </button>
                        <button type="button" className={`eh-app-item${isCorresponsalActive ? " active" : ""}`} onClick={() => { navigate("/corresponsales"); setAppMenuOpen(false); }}>
                          <span className="eh-app-item-icon eh-app-icon-corresponsales"><i className="fa-solid fa-address-book" /></span>
                          <span>Corresponsales</span>
                        </button>
                        <button type="button" className={`eh-app-item${isDesvinculacionesActive ? " active" : ""}`} onClick={() => { navigate("/materias/laboral/desvinculaciones"); setAppMenuOpen(false); }}>
                          <span className="eh-app-item-icon eh-app-icon-desvinculaciones"><i className="fa-solid fa-user-minus" /></span>
                          <span>Desvinculaciones</span>
                        </button>
                        <button type="button" className={`eh-app-item${isEmpresaActive ? " active" : ""}`} onClick={() => { navigate("/empresas"); setAppMenuOpen(false); }}>
                          <span className="eh-app-item-icon eh-app-icon-empresas"><i className="fa-solid fa-city" /></span>
                          <span>Empresas</span>
                        </button>
                        <button type="button" className={`eh-app-item${isInformesActive ? " active" : ""}`} onClick={() => { navigate("/informes"); setAppMenuOpen(false); }}>
                          <span className="eh-app-item-icon eh-app-icon-autoridades"><i className="fa-solid fa-chart-simple" /></span>
                          <span>Informes</span>
                        </button>
                        <button type="button" className={`eh-app-item${isUsuariosActive ? " active" : ""}`} onClick={() => { navigate("/usuarios"); setAppMenuOpen(false); }}>
                          <span className="eh-app-item-icon eh-app-icon-usuarios"><i className="fa-solid fa-users" /></span>
                          <span>Usuarios</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <Popover
                  open={notifOpen}
                  onOpenChange={setNotifOpen}
                  trigger="click"
                  placement="bottomRight"
                  overlayClassName="eh-popover"
                  content={notifCard}
                >
                  <button
                    type="button"
                    className="eh-ico-btn eh-bell"
                    aria-label="Notificaciones"
                    onClick={() => setUserOpen(false)}
                  >
                    <i className="bi bi-bell-fill" />
                    {unreadCount > 0 && (
                    <span className="eh-badge">{unreadCount > 99 ? "99+" : unreadCount}</span>
                  )}
                  </button>
                </Popover>

                {canShowUser && (
                  <Popover
                    open={userOpen}
                    onOpenChange={setUserOpen}
                    trigger="click"
                    placement="bottomRight"
                    overlayClassName="eh-popover"
                    content={userCard}
                  >
                    <button
                      type="button"
                      className="eh-avatar"
                      aria-label="Usuario"
                      onClick={() => setNotifOpen(false)}
                    >
                      {userInitials}
                    </button>
                  </Popover>
                )}
              </>
            )}

            {isElectron && (
              <div className="win-controls">
                <button className="electron-btn" title="Minimizar" onClick={() => window.electronAPI?.minimize?.()}>🗕</button>
                <button className="electron-btn" title="Maximizar/Restaurar" onClick={() => window.electronAPI?.maximize?.()}>🗖</button>
                <button className="electron-btn electron-btn-close" title="Cerrar" onClick={() => window.electronAPI?.close?.()}>✕</button>
              </div>
            )}
          </div>
        </div>

        <div className="eh-drag-overlay" />
      </header>

      <BuscarExpedienteModal
        open={isBuscarOpen}
        onClose={() => setIsBuscarOpen(false)}
        onSearch={(payload) => { console.log("Buscar expediente:", payload); }}
      />
    </>
  );
}
